import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button/Button';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import { PortalUserService } from '../../../services/PortalUserService';
import type { PortalRole } from '../../../types/portalUser';
import { PORTAL_ROLE_LABELS } from '../../../types/portalUser';
import DataTable from '../../../components/common/DataTable/DataTable';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import Loading from '../../../components/common/Loading/Loading';
import PageSizeSelect from '../../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../../components/common/Pagination/Pagination';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import type { PaginationInfo } from '../../../types';
import { formatDate } from '../../../utils';
import styles from './UsersTab.module.css';

const INITIAL_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const ROLE_LABELS = PORTAL_ROLE_LABELS;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const USER_FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'usersPage' }),
  pageSize: queryFilter.integer(10, { allowed: PAGE_SIZE_OPTIONS, param: 'usersPageSize' }),
  search: queryFilter.string('', { param: 'usersSearch' }),
};

const UsersTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filters, setFilter } = useQueryFilters(USER_FILTER_SCHEMA, { pageKey: 'page' });
  const { page, pageSize, search: searchValue } = filters;

  const [rows, setRows] = useState<Array<{ id: number; full_name: string; roles: PortalRole[]; is_active: boolean; created_at: string; user_id: string }>>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const fetchUsers = useCallback(async (targetPage: number, targetPageSize: number, targetSearch: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await PortalUserService.listPaginated(targetPage, targetPageSize, {
        search: targetSearch || undefined,
      });

      setRows(response.data);
      setPagination(response.pagination);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : 'Erro ao carregar usuárias do portal.';
      setError(message);
      setRows([]);
      setPagination((prev) => ({ ...prev, page: targetPage, pageSize: targetPageSize, totalRecords: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const routeState = location.state as Record<string, unknown> | null;
    const maybeMessage = routeState?.usersSuccessMessage;

    if (typeof maybeMessage === 'string' && maybeMessage) {
      setSuccessMessage(maybeMessage);
      const { usersSuccessMessage: _consumedMessage, ...remainingState } = routeState ?? {};
      navigate(
        { pathname: location.pathname, search: location.search },
        {
          replace: true,
          state: Object.keys(remainingState).length > 0 ? remainingState : null,
        },
      );
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    fetchUsers(page, pageSize, searchValue.trim());
  }, [fetchUsers, page, pageSize, searchValue]);

  const handleClearFilters = useCallback(() => {
    setFilter('search', '');
  }, [setFilter]);

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <SectionTitle as="h2" text="Usuários" />
        <Button
          variant="primary"
          size="small"
          onClick={() => navigate('/administracao/usuarios/cadastro')}
        >
          Cadastrar
        </Button>
      </div>

      {successMessage && <AlertBanner variant="success" message={successMessage} className={styles.alert} />}
      {error && <AlertBanner variant="error" message={error} className={styles.alert} />}

      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          count={`${pagination.totalRecords} ${pagination.totalRecords === 1 ? 'usuária' : 'usuárias'}`}
          className={styles['record-count']}
        />
        <FilterGroup>
          <SearchFilterBar
            searchValue={searchValue}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        {searchValue && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState icon="group_off" message="Nenhuma usuária encontrada." />
      ) : (
        <>
          <DataTable
            headers={['Nome', 'Perfil', 'Ativo', 'Criado em']}
            gridTemplate="1.8fr 1.2fr 100px 140px"
            highlightTerm={searchValue}
            borderedRows
            boldHeaders
          >
            {rows.map((row) => (
              <DataTable.Row key={row.id}>
                <button
                  type="button"
                  className={styles['name-link']}
                  onClick={() => navigate(`/administracao/usuarios/${row.user_id}/editar`)}
                >
                  {row.full_name}
                </button>
                <span>
                  {(row.roles ?? []).length > 0
                    ? (row.roles ?? []).map((role) => ROLE_LABELS[role]).join(', ')
                    : '—'}
                </span>
                <span>
                  <span className={row.is_active ? styles['badge-active'] : styles['badge-inactive']}>
                    {row.is_active ? 'Sim' : 'Não'}
                  </span>
                </span>
                <span>{formatDate(row.created_at)}</span>
              </DataTable.Row>
            ))}
          </DataTable>

          <div className={styles['bottom-row']}>
            <PageSizeSelect
              value={pageSize}
              options={PAGE_SIZE_OPTIONS}
              onChange={(event) => setFilter('pageSize', Number.parseInt(event.target.value, 10))}
            />
            <div className={styles['pagination-center']}>
              <Pagination
                pagination={pagination}
                loading={loading}
                onPageChange={(targetPage) => setFilter('page', targetPage, { resetPage: false })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UsersTab;
